import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Image, Modal } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import Select from "react-select";
import { FaCheck, FaCopy, FaExternalLinkAlt, FaLink, FaPlus, FaTimes } from "react-icons/fa";
import {
  AddProductEntrepreneur,
  analyzeProductOfferWithAi,
  getAllPartiners,
  getAllRibbonBadges,
} from "../../services/api";
import useCategories from "../../hooks/useCategories";
import useNotification from "../../contexts/notification";
import ProductImageGalleryModal from "../ProductImageGalleryModal";
import "./styles.scss";

const MAIN_PLATFORMS = [
  { value: "amazon", label: "Amazon", emoji: "📦" },
  { value: "ml", label: "Mercado Livre", emoji: "🟡" },
  { value: "shopee", label: "Shopee", emoji: "🟠" },
  { value: "magalu", label: "Magalu", emoji: "🔵" },
];

const OTHER_PLATFORMS = ["Shoptime", "Americanas", "Casas Bahia", "Kabum", "AliExpress"];

const MESSAGE_STYLES = [
  { value: "animado", label: "Animado", emoji: "🎉" },
  { value: "apressado", label: "Apressado", emoji: "🔥" },
  { value: "formal", label: "Formal", emoji: "💼" },
  { value: "divertido", label: "Engraçado", emoji: "😄" },
  { value: "urgente", label: "Urgente", emoji: "🚨" },
];

const initialForm = {
  link: "",
  product_query: "",
  name: "",
  description: "",
  current_price: "",
  previous_price: "",
  partner_id: "",
  category_type_id: "",
  ribbon_badges: "0",
  image: null,
  image_source_url: "",
  platform_name: "",
  custom_title: "",
  installment_value: "",
  installment_count: "",
  installment_type: "",
  only_pix: false,
  same_as_cash: false,
  free_shipping: false,
  no_coupon_code: false,
  shopee_redeem_link: false,
  shopee_redeem_url: "",
};

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function buildFormData(payload) {
  const data = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => data.append(`${key}[]`, item));
      return;
    }
    if (typeof value === "boolean") {
      data.append(key, value ? "1" : "0");
      return;
    }
    data.append(key, value);
  });
  return data;
}

let couponIdSeq = 0;
function makeCoupon(code = "") {
  couponIdSeq += 1;
  return { id: `coupon-${couponIdSeq}`, code };
}

export default function AddProductAssistantForm() {
  const categories = useCategories();
  const { setMessage } = useNotification();
  const [form, setForm] = useState(initialForm);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [otherPlatform, setOtherPlatform] = useState("");
  const [partners, setPartners] = useState([]);
  const [badges, setBadges] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [status, setStatus] = useState(null);
  const [coupons, setCoupons] = useState([makeCoupon("")]);
  const [selectedMessageStyle, setSelectedMessageStyle] = useState("");
  const [selectedMessageVersion, setSelectedMessageVersion] = useState("1");
  const [resultModal, setResultModal] = useState({ open: false, shortUrl: "", messages: {} });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [isApplyingGalleryImage, setIsApplyingGalleryImage] = useState(false);
  const [copyState, setCopyState] = useState({ link: false, message: false });
  const [messageEdits, setMessageEdits] = useState({});
  const fileInputRef = useRef(null);

  const groupedCategoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        label: category.name,
        options: category.category_types.map((subCategory) => ({
          label: subCategory.name,
          value: String(subCategory.id),
        })),
      })),
    [categories]
  );

  const flatCategoryOptions = useMemo(
    () => groupedCategoryOptions.flatMap((group) => group.options),
    [groupedCategoryOptions]
  );

  const selectedCategoryOption = useMemo(
    () => flatCategoryOptions.find((option) => option.value === String(form.category_type_id || "")) || null,
    [flatCategoryOptions, form.category_type_id]
  );

  const partnerOptions = useMemo(
    () =>
      partners.map((partner) => ({
        value: String(partner.id),
        label: partner.name,
      })),
    [partners]
  );

  const selectedPartnerOption = useMemo(
    () => partnerOptions.find((option) => option.value === String(form.partner_id || "")) || null,
    [partnerOptions, form.partner_id]
  );

  useEffect(() => {
    getAllRibbonBadges().then(setBadges);
    getAllPartiners().then(setPartners);
  }, []);

  function findPartnerByName(name) {
    const normalized = normalizeText(name);
    if (!normalized) {
      return null;
    }

    return (
      partners.find((partner) => normalizeText(partner.name) === normalized) ||
      partners.find((partner) => normalizeText(partner.name).includes(normalized)) ||
      partners.find((partner) => normalized.includes(normalizeText(partner.name))) ||
      null
    );
  }

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function handleSelectMainPlatform(platform) {
    setSelectedPlatform(platform.value);
    setOtherPlatform("");
    updateForm("platform_name", platform.label);

    const matched = findPartnerByName(platform.label);
    if (matched) {
      updateForm("partner_id", String(matched.id));
    }
  }

  function handleSelectOtherPlatform(name) {
    if (!name) {
      return;
    }
    setSelectedPlatform("outro");
    setOtherPlatform(name);
    updateForm("platform_name", name);

    const matched = findPartnerByName(name);
    if (matched) {
      updateForm("partner_id", String(matched.id));
    }
  }

  function handleLinkChange(value) {
    updateForm("link", value);
    const normalized = value.toLowerCase();

    if (selectedPlatform) {
      return;
    }

    const detections = [
      { keys: ["amazon", "amzn"], platform: MAIN_PLATFORMS[0] },
      { keys: ["mercadolivre", "mercado", "mlb"], platform: MAIN_PLATFORMS[1] },
      { keys: ["shopee", "shope.ee"], platform: MAIN_PLATFORMS[2] },
      { keys: ["magalu", "magazineluiza"], platform: MAIN_PLATFORMS[3] },
    ];

    const found = detections.find((entry) => entry.keys.some((k) => normalized.includes(k)));
    if (found) {
      handleSelectMainPlatform(found.platform);
      return;
    }

    const otherMatch = OTHER_PLATFORMS.find((name) => normalized.includes(normalizeText(name).replace(/\s+/g, "")));
    if (otherMatch) {
      handleSelectOtherPlatform(otherMatch);
    }
  }

  async function handleAnalyzeOffer() {
    if (!form.link.trim()) {
      setMessage({ error: true, content: "Cole o link da oferta antes de preencher." });
      return;
    }

    if (!form.product_query.trim()) {
      setMessage({ error: true, content: "Informe o título do produto para a IA localizar melhor." });
      return;
    }

    setIsAnalyzing(true);
    setStatus({ type: "loading", message: "Buscando dados do produto no link informado..." });

    try {
      const response = await analyzeProductOfferWithAi({
        link: form.link.trim(),
        product_name: form.product_query.trim(),
        platform: form.platform_name || selectedPlatform || "",
      });

      const nextAnalysis = response.analysis || null;
      setAnalysis(nextAnalysis);

      updateForm("name", form.product_query.trim() || nextAnalysis?.name || "");
      if (nextAnalysis?.description) {
        updateForm("description", nextAnalysis.description);
      }
      if (nextAnalysis?.image_url && !form.image) {
        updateForm("image_source_url", nextAnalysis.image_url);
      }
      if (nextAnalysis?.category_hint) {
        const normalizedHint = normalizeText(nextAnalysis.category_hint);
        const matched =
          flatCategoryOptions.find((opt) => normalizeText(opt.label).includes(normalizedHint)) ||
          flatCategoryOptions.find((opt) => normalizedHint.includes(normalizeText(opt.label)));
        if (matched && !form.category_type_id) {
          updateForm("category_type_id", matched.value);
        }
      }

      setStatus({ type: "success", message: "Produto identificado. Revise os dados, ajuste o preço e publique." });
    } catch (error) {
      console.error(error);
      setStatus({ type: "error", message: "Não foi possível preencher automaticamente agora. Você ainda pode completar manualmente." });
      setMessage({ error: true, content: "Falha ao analisar o link da oferta." });
    } finally {
      setIsAnalyzing(false);
    }
  }

  function handleImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setSelectedGalleryImage(null);
    updateForm("image", file);
    updateForm("image_source_url", "");
  }

  const imagePreview = useMemo(() => {
    if (form.image instanceof File) {
      return URL.createObjectURL(form.image);
    }

    if (selectedGalleryImage?.thumbnail_url || selectedGalleryImage?.url) {
      return selectedGalleryImage.thumbnail_url || selectedGalleryImage.url;
    }

    return form.image_source_url || analysis?.image_url || "";
  }, [analysis?.image_url, form.image, form.image_source_url, selectedGalleryImage]);

  useEffect(() => {
    return () => {
      if (form.image instanceof File && imagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [form.image, imagePreview]);

  async function copyToClipboard(type, value) {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopyState((current) => ({ ...current, [type]: true }));
      setTimeout(() => {
        setCopyState((current) => ({ ...current, [type]: false }));
      }, 1800);
    } catch (error) {
      console.error(error);
      setMessage({ error: true, content: "Não foi possível copiar agora." });
    }
  }

  async function handleSelectGalleryImage(image) {
    setIsApplyingGalleryImage(true);

    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const file = new File([blob], image.file_name || `imagem-${image.id}.jpg`, {
        type: blob.type || "image/jpeg",
      });

      updateForm("image", file);
      updateForm("image_source_url", "");
      setSelectedGalleryImage(image);
      setShowGalleryModal(false);
    } catch (error) {
      console.error(error);
      setMessage({ error: true, content: "Não foi possível usar a imagem da galeria." });
    } finally {
      setIsApplyingGalleryImage(false);
    }
  }

  function addCoupon() {
    setCoupons((current) => [...current, makeCoupon("")]);
  }

  function removeCoupon(id) {
    setCoupons((current) => {
      const next = current.filter((c) => c.id !== id);
      return next.length ? next : [makeCoupon("")];
    });
  }

  function updateCoupon(id, code) {
    setCoupons((current) => current.map((c) => (c.id === id ? { ...c, code } : c)));
  }

  function handleNoCouponCode(checked) {
    updateForm("no_coupon_code", checked);
    if (checked) {
      updateForm("shopee_redeem_link", false);
      setCoupons([makeCoupon("")]);
    }
  }

  function handleShopeeRedeem(checked) {
    updateForm("shopee_redeem_link", checked);
    if (checked) {
      updateForm("no_coupon_code", false);
    } else {
      updateForm("shopee_redeem_url", "");
    }
  }

  function handleOnlyPixChange(checked) {
    updateForm("only_pix", checked);
    if (checked) {
      updateForm("same_as_cash", false);
      updateForm("installment_value", "");
    }
  }

  function handleSameAsCash(checked) {
    updateForm("same_as_cash", checked);
    if (checked) {
      updateForm("only_pix", false);
      updateForm("installment_value", form.current_price);
    } else {
      updateForm("installment_value", "");
    }
  }

  function handleInstallmentType(type) {
    updateForm("installment_type", form.installment_type === type ? "" : type);
  }

  const activeCoupons = useMemo(
    () =>
      coupons
        .map((c) => c.code.trim().toUpperCase())
        .filter(Boolean),
    [coupons]
  );

  function validateForm() {
    if (!selectedPlatform) {
      return "Selecione o parceiro da oferta.";
    }
    if (!form.link.trim()) {
      return "Cole o link da oferta.";
    }
    if (!form.name.trim()) {
      return "Preencha o título do produto.";
    }
    if (!form.description.trim()) {
      return "Preencha a descrição do produto.";
    }
    if (!form.category_type_id) {
      return "Selecione a subcategoria.";
    }
    if (!form.current_price) {
      return "Informe o preço atual da oferta.";
    }
    if (!form.partner_id) {
      return "Selecione o parceiro cadastrado da oferta.";
    }
    if (!form.image && !form.image_source_url) {
      return "Adicione uma imagem por upload ou galeria.";
    }
    return null;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const errorMessage = validateForm();
    if (errorMessage) {
      setMessage({ error: true, content: errorMessage });
      return;
    }

    setIsSubmitting(true);

    const couponSummary = activeCoupons.join(" + ");
    const hasCouponInfo = activeCoupons.length > 0 || form.no_coupon_code || form.shopee_redeem_link;

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      image: form.image,
      image_source_url: form.image ? "" : form.image_source_url,
      previous_price: form.previous_price || "",
      offer_price: form.current_price || "",
      link: form.link.trim(),
      partner_id: Number(form.partner_id),
      have_cupom: hasCouponInfo ? "1" : "0",
      send_notifications: "0",
      title: form.custom_title.trim(),
      ribbon_badges: Number(form.ribbon_badges || 0),
      category_type_id: Number(form.category_type_id),
      cupom: couponSummary,
      cupom_validity: "",
      price_on_offer: form.current_price ? "1" : "0",
      generate_offer_messages: "1",
      platform_name: form.platform_name,
      message_highlights: (analysis?.highlights || []).join("\n"),
      custom_title: form.custom_title.trim(),
      only_pix: form.only_pix,
      free_shipping: form.free_shipping,
      installment_value: form.installment_value || "",
      installment_count: form.installment_count || "",
      installment_type: form.installment_type || "",
      same_as_cash: form.same_as_cash,
      no_coupon_code: form.no_coupon_code,
      shopee_redeem_link: form.shopee_redeem_link,
      shopee_redeem_url: form.shopee_redeem_link ? form.shopee_redeem_url.trim() : "",
      coupons: activeCoupons,
      message_tone: selectedMessageStyle || "",
    };

    try {
      const response = await AddProductEntrepreneur(buildFormData(payload));
      if (response?.error || !response?.product) {
        throw response?.error || new Error("Falha ao salvar a oferta.");
      }

      setMessage({ error: false, content: response.msg || "Oferta cadastrada com sucesso." });
      setResultModal({
        open: true,
        shortUrl: response.shortUrl || "",
        messages: response.generatedMessages || {},
      });
      setCopyState({ link: false, message: false });
      setMessageEdits({});
      setSelectedMessageVersion("1");
      setAnalysis(null);
      setSelectedGalleryImage(null);
      setCoupons([makeCoupon("")]);
      setSelectedPlatform(null);
      setOtherPlatform("");
      setSelectedMessageStyle("");
      setStatus(null);
      setForm(initialForm);
    } catch (error) {
      console.error(error);
      setMessage({ error: true, content: "Erro ao adicionar a oferta." });
    } finally {
      setIsSubmitting(false);
    }
  }

  const availableMessageVersions = useMemo(
    () =>
      ["1", "2", "3"].filter((version) =>
        version === "1"
          ? resultModal.messages?.[selectedMessageStyle] || resultModal.messages?.[`${selectedMessageStyle}_1`]
          : resultModal.messages?.[`${selectedMessageStyle}_${version}`]
      ),
    [resultModal.messages, selectedMessageStyle]
  );

  const activeMessageKey = `${selectedMessageStyle}_${selectedMessageVersion}`;

  const generatedActiveMessage =
    resultModal.messages?.[activeMessageKey] ||
    resultModal.messages?.[selectedMessageStyle] ||
    resultModal.messages?.[`${selectedMessageStyle}_1`] ||
    "";

  const activeMessage =
    messageEdits[activeMessageKey] !== undefined
      ? messageEdits[activeMessageKey]
      : generatedActiveMessage;

  function handleMessageEdit(value) {
    if (!selectedMessageStyle) {
      return;
    }
    setMessageEdits((current) => ({ ...current, [activeMessageKey]: value }));
  }

  function resetMessage() {
    setMessageEdits((current) => {
      const next = { ...current };
      delete next[activeMessageKey];
      return next;
    });
  }

  const isEdited =
    messageEdits[activeMessageKey] !== undefined &&
    messageEdits[activeMessageKey] !== generatedActiveMessage;

  const titleCount = form.custom_title.length;

  return (
    <>
      <div className="offer-assistant-page">
        <div className="offer-assistant-hero">
          <span className="offer-assistant-badge">Jersu Indica com IA</span>
          <h1>Adicionar oferta com preenchimento inteligente</h1>
          <p>
            Monte ofertas no padrão do Jersu Indica com busca no link, upload de imagem,
            galeria, emblemas e mensagem pronta para disparar no WhatsApp em poucos cliques.
          </p>
        </div>

        <Form onSubmit={handleSubmit} className="offer-assistant-stack">
          <section className="offer-assistant-card">
            <div className="offer-assistant-title">Parceiros</div>

            <div className="offer-platform-grid">
              {MAIN_PLATFORMS.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={`offer-platform-button ${selectedPlatform === option.value ? "is-active" : ""}`}
                  onClick={() => handleSelectMainPlatform(option)}
                >
                  <span>{option.emoji}</span>
                  <strong>{option.label}</strong>
                </button>
              ))}
              <div className={`offer-platform-button offer-platform-other ${selectedPlatform === "outro" ? "is-active" : ""}`}>
                <select
                  value={otherPlatform}
                  onChange={(event) => handleSelectOtherPlatform(event.target.value)}
                >
                  <option value="" disabled>
                    Outro
                  </option>
                  {OTHER_PLATFORMS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                {otherPlatform ? <strong>{otherPlatform}</strong> : null}
              </div>
            </div>

            <Form.Group className="offer-field">
              <Form.Label>
                Parceiro cadastrado <span className="offer-required">*</span>
              </Form.Label>
              <Select
                inputId="assistant-partner-id"
                name="partner_id"
                options={partnerOptions}
                value={selectedPartnerOption}
                onChange={(option) => updateForm("partner_id", option ? option.value : "")}
                placeholder="Selecione o parceiro cadastrado"
                noOptionsMessage={() => "Nenhum parceiro encontrado"}
                classNamePrefix="select"
                isClearable
              />
            </Form.Group>

            <Form.Group className="offer-field">
              <Form.Label>
                Link da oferta <span className="offer-required">*</span>
              </Form.Label>
              <Form.Control
                type="url"
                value={form.link}
                onChange={(event) => handleLinkChange(event.target.value)}
                placeholder="https://..."
              />
            </Form.Group>

            <Form.Group className="offer-field">
              <Form.Label>
                Título da oferta <span className="offer-required">*</span>
              </Form.Label>
              <div className="offer-inline-action">
                <Form.Control
                  type="text"
                  value={form.product_query || form.name}
                  onChange={(event) => {
                    updateForm("product_query", event.target.value);
                    updateForm("name", event.target.value);
                  }}
                  placeholder="Insira o título"
                />
                <Button type="button" onClick={handleAnalyzeOffer} disabled={isAnalyzing}>
                  {isAnalyzing ? "Buscando..." : "Buscar oferta"}
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="offer-field">
              <Form.Label>
                Subcategoria <span className="offer-required">*</span>
              </Form.Label>
              <Select
                inputId="assistant-category-type-id"
                name="category_type_id"
                options={groupedCategoryOptions}
                value={selectedCategoryOption}
                onChange={(option) => updateForm("category_type_id", option ? option.value : "")}
                placeholder="Selecione ou digite para pesquisar..."
                noOptionsMessage={() => "Nenhuma subcategoria encontrada"}
                classNamePrefix="select"
                isClearable
              />
            </Form.Group>

            <Form.Group className="offer-field">
              <Form.Label>
                Descrição <span className="offer-required">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={form.description}
                onChange={(event) => updateForm("description", event.target.value)}
                placeholder="Insira a descrição"
              />
            </Form.Group>

            <Form.Check
              type="checkbox"
              id="free-shipping-check"
              label="Frete grátis?"
              checked={form.free_shipping}
              onChange={(event) => updateForm("free_shipping", event.target.checked)}
              className="offer-check"
            />

            {status && <div className={`offer-status offer-status-${status.type}`}>{status.message}</div>}
          </section>

          <section className="offer-assistant-card">
            <div className="offer-assistant-title">
              Preço <span className="offer-required">*</span>
            </div>

            <Form.Group className="offer-field offer-price-field">
              <Form.Label>
                Preço atual <span className="offer-required">*</span>
              </Form.Label>
              <NumericFormat
                value={form.current_price}
                customInput={Form.Control}
                thousandSeparator="."
                decimalSeparator=","
                allowNegative={false}
                decimalScale={2}
                fixedDecimalScale={false}
                prefix="R$ "
                onValueChange={({ floatValue }) => {
                  const newValue = floatValue ?? "";
                  updateForm("current_price", newValue);
                  if (form.same_as_cash) {
                    updateForm("installment_value", newValue);
                  }
                }}
                placeholder="R$ 0,00"
              />
            </Form.Group>

            <Form.Check
              type="checkbox"
              id="only-pix-check"
              label="Valor atual é somente no Pix?"
              checked={form.only_pix}
              onChange={(event) => handleOnlyPixChange(event.target.checked)}
              disabled={form.same_as_cash}
              className="offer-check"
            />

            <hr className="offer-divider" />

            <div className="offer-subtitle">
              Parcelamento <span className="offer-optional">(opcional)</span>
            </div>

            <div className="offer-installment-row">
              <div className="offer-field offer-installment-value">
                <label>Valor parcelado</label>
                <NumericFormat
                  value={form.installment_value}
                  customInput={Form.Control}
                  thousandSeparator="."
                  decimalSeparator=","
                  allowNegative={false}
                  decimalScale={2}
                  fixedDecimalScale={false}
                  prefix="R$ "
                  onValueChange={({ floatValue }) => updateForm("installment_value", floatValue ?? "")}
                  placeholder="R$ 0,00"
                  disabled={form.same_as_cash}
                />
              </div>

              <Form.Check
                type="checkbox"
                id="same-as-cash-check"
                label="Valor de à vista"
                checked={form.same_as_cash}
                onChange={(event) => handleSameAsCash(event.target.checked)}
                disabled={form.only_pix}
                className="offer-check offer-check-inline"
              />
            </div>

            <div className="offer-installment-options">
              <Form.Check
                type="checkbox"
                id="installment-with-interest"
                label="Com juros"
                checked={form.installment_type === "com_juros"}
                onChange={() => handleInstallmentType("com_juros")}
                className="offer-check offer-check-inline"
              />
              <Form.Check
                type="checkbox"
                id="installment-without-interest"
                label="Sem juros"
                checked={form.installment_type === "sem_juros"}
                onChange={() => handleInstallmentType("sem_juros")}
                className="offer-check offer-check-inline"
              />
              <div className="offer-installment-count">
                <Form.Control
                  type="text"
                  value={form.installment_count}
                  onChange={(event) => {
                    const cleaned = event.target.value.replace(/\D/g, "").slice(0, 2);
                    updateForm("installment_count", cleaned);
                  }}
                  placeholder="parcelas"
                />
                <span>x</span>
              </div>
            </div>

            <hr className="offer-divider" />

            <div className="offer-subtitle">
              Cupom <span className="offer-optional">(opcional)</span>
            </div>

            <div className="offer-coupon-list">
              {coupons.map((coupon, index) => (
                <div key={coupon.id} className="offer-coupon-row">
                  <Form.Control
                    type="text"
                    value={coupon.code}
                    onChange={(event) => updateCoupon(coupon.id, event.target.value.toUpperCase())}
                    placeholder="Insira o cupom"
                    disabled={form.no_coupon_code}
                  />
                  {index === coupons.length - 1 && !form.no_coupon_code ? (
                    <button
                      type="button"
                      className="offer-coupon-add"
                      onClick={addCoupon}
                      title="Adicionar cupom"
                    >
                      <FaPlus />
                    </button>
                  ) : null}
                  {coupons.length > 1 ? (
                    <button
                      type="button"
                      className="offer-coupon-remove"
                      onClick={() => removeCoupon(coupon.id)}
                      title="Remover"
                    >
                      <FaTimes />
                    </button>
                  ) : null}
                </div>
              ))}
            </div>

            <Form.Check
              type="checkbox"
              id="no-coupon-code-check"
              label="Sem código nominal (ativar abaixo do preço)"
              checked={form.no_coupon_code}
              onChange={(event) => handleNoCouponCode(event.target.checked)}
              className="offer-check"
            />

            <Form.Check
              type="checkbox"
              id="shopee-redeem-check"
              label="Link de resgate da Shopee"
              checked={form.shopee_redeem_link}
              onChange={(event) => handleShopeeRedeem(event.target.checked)}
              className="offer-check"
            />

            {form.shopee_redeem_link ? (
              <Form.Group className="offer-field offer-shopee-url">
                <Form.Label>Link de resgate</Form.Label>
                <Form.Control
                  type="url"
                  value={form.shopee_redeem_url}
                  onChange={(event) => updateForm("shopee_redeem_url", event.target.value)}
                  placeholder="Cole o link de resgate do cupom"
                />
              </Form.Group>
            ) : null}
          </section>

          <section className="offer-assistant-card">
            <div className="offer-assistant-title">
              Personalizar título da mensagem <span className="offer-optional">(opcional)</span>
            </div>

            <Form.Group className="offer-field offer-custom-title">
              <Form.Control
                type="text"
                value={form.custom_title}
                onChange={(event) => updateForm("custom_title", event.target.value.slice(0, 60))}
                placeholder="Insira o título manualmente (opção preferencial)"
              />
              <span className="offer-char-count">{titleCount}/60</span>
            </Form.Group>

            <div className="offer-tone-label">
              Ou gere automaticamente <span className="offer-optional">(opcional)</span>
            </div>

            <div className="offer-style-grid">
              {MESSAGE_STYLES.map((style) => (
                <button
                  type="button"
                  key={style.value}
                  className={`${selectedMessageStyle === style.value ? "is-active" : ""} ${
                    style.value === "urgente" ? "is-urgent" : ""
                  }`}
                  onClick={() =>
                    setSelectedMessageStyle(selectedMessageStyle === style.value ? "" : style.value)
                  }
                >
                  <span>{style.emoji}</span>
                  <strong>{style.label}</strong>
                </button>
              ))}
            </div>
          </section>

          <section className="offer-assistant-card">
            <div className="offer-assistant-title">
              Imagem do produto <span className="offer-required">*</span>
            </div>

            <div className="offer-image-actions">
              <Button type="button" variant="outline-primary" onClick={() => fileInputRef.current?.click()}>
                Fazer upload
              </Button>
              <Button type="button" variant="outline-primary" onClick={() => setShowGalleryModal(true)}>
                Usar galeria
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/avif,image/webp,image/png,image/jpeg"
                hidden
                onChange={handleImageUpload}
              />
            </div>

            {imagePreview ? (
              <div className="offer-image-preview">
                <Image src={imagePreview} alt={form.name || "Imagem da oferta"} fluid rounded />
                {selectedGalleryImage?.product_name ? <small>{selectedGalleryImage.product_name}</small> : null}
              </div>
            ) : (
              <div className="offer-image-empty">Nenhuma imagem selecionada</div>
            )}

            <div className="offer-field">
              <label>Emblema</label>
              <div className="offer-badge-grid">
                <button
                  type="button"
                  className={form.ribbon_badges === "0" ? "is-active" : ""}
                  onClick={() => updateForm("ribbon_badges", "0")}
                >
                  Sem emblema
                </button>
                {badges.map((badge) => (
                  <button
                    type="button"
                    key={badge.id}
                    className={form.ribbon_badges === String(badge.id) ? "is-active" : ""}
                    onClick={() => updateForm("ribbon_badges", String(badge.id))}
                  >
                    {badge.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <div className="offer-submit-row">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Adicionando oferta..." : "Adicionar Oferta com IA"}
            </Button>
          </div>
        </Form>
      </div>

      <Modal show={resultModal.open} onHide={() => setResultModal((current) => ({ ...current, open: false }))} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Oferta publicada e mensagem pronta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="offer-result-shorturl">
            <label>Link curto do post</label>
            <div>
              <div className={`offer-copy-input ${copyState.link ? "is-copied" : ""}`}>
                <span className="offer-copy-input-icon">
                  <FaLink />
                </span>
                <Form.Control value={resultModal.shortUrl} readOnly />
              </div>
              <Button
                type="button"
                className={`offer-copy-button ${copyState.link ? "is-copied" : ""}`}
                onClick={() => copyToClipboard("link", resultModal.shortUrl || "")}
              >
                {copyState.link ? <FaCheck /> : <FaCopy />}
                <span>{copyState.link ? "Link copiado" : "Copiar link"}</span>
              </Button>
            </div>
          </div>

          <div className="offer-style-grid offer-style-grid-modal">
            {MESSAGE_STYLES.filter(
              (style) =>
                resultModal.messages?.[style.value] ||
                resultModal.messages?.[`${style.value}_1`] ||
                resultModal.messages?.[`${style.value}_2`] ||
                resultModal.messages?.[`${style.value}_3`]
            ).map((style) => (
              <button
                type="button"
                key={style.value}
                className={`${selectedMessageStyle === style.value ? "is-active" : ""} ${
                  style.value === "urgente" ? "is-urgent" : ""
                }`}
                onClick={() => {
                  setSelectedMessageStyle(style.value);
                  setSelectedMessageVersion("1");
                }}
              >
                <span>{style.emoji}</span>
                <strong>{style.label}</strong>
              </button>
            ))}
          </div>

          {availableMessageVersions.length > 0 ? (
            <div className="offer-message-version-grid">
              {availableMessageVersions.map((version) => (
                <button
                  type="button"
                  key={version}
                  className={`offer-message-version-button ${selectedMessageVersion === version ? "is-active" : ""}`}
                  onClick={() => setSelectedMessageVersion(version)}
                >
                  Versão {version}
                </button>
              ))}
            </div>
          ) : null}

          <Form.Control
            as="textarea"
            className="offer-result-message"
            value={activeMessage}
            onChange={(event) => handleMessageEdit(event.target.value)}
            placeholder="A mensagem será exibida aqui."
            disabled={!selectedMessageStyle}
          />
          {isEdited ? (
            <button type="button" className="offer-reset-message" onClick={resetMessage}>
              Restaurar mensagem original
            </button>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="offer-modal-footer">
          <Button
            type="button"
            variant={copyState.message ? "success" : "outline-primary"}
            className={`offer-copy-button offer-copy-message-button ${copyState.message ? "is-copied" : ""}`}
            onClick={() => copyToClipboard("message", activeMessage || "")}
          >
            {copyState.message ? <FaCheck /> : <FaCopy />}
            <span>{copyState.message ? "Mensagem copiada" : "Copiar mensagem"}</span>
          </Button>
          <Button type="button" className="offer-open-post-button" onClick={() => window.open(resultModal.shortUrl, "_blank")}>
            <FaExternalLinkAlt />
            <span>Ver post</span>
          </Button>
        </Modal.Footer>
      </Modal>

      <ProductImageGalleryModal
        show={showGalleryModal}
        onHide={() => !isApplyingGalleryImage && setShowGalleryModal(false)}
        onSelect={handleSelectGalleryImage}
        selectedImageId={selectedGalleryImage?.id}
        isConfirmingSelection={isApplyingGalleryImage}
      />
    </>
  );
}
